import { rollupWeightedScores } from 'js/utils/question-set';
import {
  getOrgWeightConfig,
  normalizeOrgForWeights,
  orgWeightResolutionKey,
  parentOrgIdForRow,
  resolveOrgWeights,
  weightsRootOrganizationId,
} from 'js/routes/Organizations/components/ManageOrg/orgWeightsUtils';

/**
 * Weight context for rolling up assessment scores through an org tree.
 * Uses the root org's weight_scheme and org_weights placements.
 */
export function createOrgAssessmentWeightContext(organization) {
  if (!organization?.id) {
    return null;
  }

  const tree = normalizeOrgForWeights(organization);
  if (!tree) {
    return null;
  }

  const config = getOrgWeightConfig(organization);
  const { effectiveByKey } = resolveOrgWeights(
    tree,
    config.scheme,
    config.weights,
    organization
  );

  return {
    rootOrgId: weightsRootOrganizationId(organization),
    effectiveByKey,
  };
}

/** Org rows from root → parent for weight lookup on immediate children. */
export function orgPathRowsForChildWeights(hierarchyNode) {
  const rows = [];
  let current = hierarchyNode;

  while (current) {
    const { type, id, name } = current.data ?? {};
    if (type === 'org') {
      rows.push({ id, name, type: 'org' });
    }
    current = current.parent;
  }

  return rows.reverse();
}

/**
 * Fraction of the parent's rollup each child represents (weight share among
 * siblings that have a score). Matches denominator used for weighted means.
 */
export function childContributionFractions(
  children,
  weightContext,
  pathToNode,
  { getScore }
) {
  const entries = [];

  for (const child of children ?? []) {
    const scored = getScore(child);
    if (scored?.score == null) {
      continue;
    }

    const weight = childWeight(weightContext, child.data ?? child, pathToNode);
    entries.push({
      child,
      score: scored.score,
      weight: weight > 0 ? weight : 0,
    });
  }

  const totalWeight = entries.reduce((sum, entry) => sum + entry.weight, 0);

  return entries.map((entry) => ({
    child: entry.child,
    score: entry.score,
    fraction: totalWeight > 0 ? entry.weight / totalWeight : 0,
  }));
}

/**
 * Per-question contributions for a question-set distribution (original chart
 * behavior): each scored question inherits an equal share of its child's weight.
 */
export function questionSetContributionFractions(
  children,
  weightContext,
  pathToNode,
  questionSetKey,
  { isScored = (questionType) => questionType != null }
) {
  const perChild = childContributionFractions(
    children,
    weightContext,
    pathToNode,
    {
      getScore: (child) => {
        const questions = scoredQuestionsForSet(
          child,
          questionSetKey,
          isScored
        );
        return questions.length > 0 ? { score: 0 } : null;
      },
    }
  );

  return perChild.flatMap(({ child, fraction }) => {
    const questions = scoredQuestionsForSet(child, questionSetKey, isScored);
    const share = fraction / questions.length;

    return questions.map((question) => ({
      score: Number(question.score),
      fraction: share,
    }));
  });
}

function scoredQuestionsForSet(child, questionSetKey, isScored) {
  const questions = child.data?.scores?.[questionSetKey]?.questions ?? {};
  return Object.values(questions).filter(
    (q) => q.score != null && isScored(q.question_type)
  );
}

export function childWeight(weightContext, child, pathToNode) {
  if (!weightContext) {
    return 1;
  }

  const row = {
    ...child,
    type:
      child.type ??
      (child.child_orgs != null || child.teams != null ? 'org' : 'team'),
    pathToNode,
  };

  const key = orgWeightResolutionKey(
    weightContext.rootOrgId,
    parentOrgIdForRow(row),
    row
  );
  const weight = weightContext.effectiveByKey.get(key);

  if (weight == null || !Number.isFinite(weight)) {
    return 1;
  }

  return weight >= 0 ? weight : 1;
}

/**
 * Weighted mean over siblings. Children without a score are omitted from the
 * calculation entirely (their weight does not apply). Uses configured weights,
 * not renormalized among present children only.
 */
export function rollupSiblingScores(items, { getScore, getWeight }) {
  return rollupWeightedScores(items, { getScore, getWeight });
}

/** Single aggregate score for org chart nodes (latest team-scores rollup). */
export function rollupOrgChartScore(children, weightContext, pathToNode) {
  const score = rollupSiblingScores(children, {
    getScore: (child) => child.score ?? null,
    getWeight: (child) =>
      childWeight(weightContext, chartNodeForWeight(child), pathToNode),
  });

  return score == null ? undefined : score;
}

function chartNodeForWeight(child) {
  const type =
    child.type === 'team' ? 'team' : child.type === 'org' ? 'org' : 'org';

  return {
    ...child,
    type,
    child_orgs: type === 'org' ? child.child_orgs ?? [] : undefined,
    teams: type === 'team' ? child.teams : undefined,
  };
}

function collectQuestionKeys(children) {
  const keys = new Map();

  for (const child of children) {
    for (const [qsKey, qs] of Object.entries(child.scores ?? {})) {
      for (const [qKey, question] of Object.entries(qs.questions ?? {})) {
        const id = `${qsKey}|${qKey}`;
        if (!keys.has(id)) {
          keys.set(id, {
            question_set_key: qsKey,
            question_key: qKey,
            question_type: question.question_type,
          });
        }
      }
    }
  }

  return Array.from(keys.values());
}

/**
 * Roll up scored children into org-level question set / question scores.
 */
export function rollupGroupScoresFromChildren(
  children,
  weightContext,
  pathToNode
) {
  const questionKeys = collectQuestionKeys(children);
  const scoresByQs = {};

  for (const {
    question_set_key: qsKey,
    question_key: qKey,
    question_type,
  } of questionKeys) {
    const items = children.map((child) => ({
      score: child.scores?.[qsKey]?.questions?.[qKey]?.score ?? null,
      unScore: child.scores?.[qsKey]?.questions?.[qKey]?.unScore ?? null,
      weight: childWeight(weightContext, child, pathToNode),
    }));

    const score = rollupSiblingScores(items, {
      getScore: (item) => item.score,
      getWeight: (item) => item.weight,
    });

    const unScore = rollupSiblingScores(items, {
      getScore: (item) => item.unScore ?? item.score,
      getWeight: (item) => item.weight,
    });

    if (!scoresByQs[qsKey]) {
      scoresByQs[qsKey] = { question_set_key: qsKey, questions: {} };
    }

    scoresByQs[qsKey].questions[qKey] = {
      question_key: qKey,
      question_type,
      score,
      unScore,
    };
  }

  const scores = {};

  for (const [qsKey, { questions }] of Object.entries(scoresByQs)) {
    let qsScoredTotal = 0;
    let qsScoredCount = 0;

    for (const q of Object.values(questions)) {
      if (q.score != null) {
        qsScoredTotal += q.score;
        qsScoredCount += 1;
      }
    }

    scores[qsKey] = {
      question_set_key: qsKey,
      score: qsScoredCount > 0 ? qsScoredTotal / qsScoredCount : null,
      questions: Object.fromEntries(
        Object.entries(questions).map(([qKey, q]) => [
          qKey,
          {
            question_key: q.question_key,
            question_type: q.question_type,
            score: q.score,
            unScore: q.unScore,
          },
        ])
      ),
    };
  }

  return scores;
}

export const QUESTION_SET_ROLLUP_SUFFIX = '__question_set__';

export function questionSetRollupKey(questionSetKey) {
  return `${questionSetKey}|${QUESTION_SET_ROLLUP_SUFFIX}`;
}

export function isQuestionSetRollupKey(combinedKey) {
  return combinedKey?.endsWith(`|${QUESTION_SET_ROLLUP_SUFFIX}`);
}

function averageDefinedScores(scores) {
  const valid = scores.filter((s) => s != null);
  if (!valid.length) {
    return null;
  }
  return valid.reduce((sum, score) => sum + score, 0) / valid.length;
}

/** Mean of per-question scores within one question set on a flat score map. */
export function questionSetScoreFromFlatMap(
  scores,
  questionSetKey,
  questionKeys
) {
  return averageDefinedScores(
    questionKeys.map(
      (questionKey) => scores[`${questionSetKey}|${questionKey}`]?.score
    )
  );
}

export function enrichFlatScoresWithQuestionSetRollups(
  scores,
  questionSetRollups
) {
  if (!questionSetRollups?.length) {
    return scores;
  }

  const enriched = { ...scores };

  for (const { questionSetKey, questionKeys } of questionSetRollups) {
    enriched[questionSetRollupKey(questionSetKey)] = {
      score: questionSetScoreFromFlatMap(scores, questionSetKey, questionKeys),
      question_set_key: questionSetKey,
    };
  }

  return enriched;
}

export function rollupChildQuestionSetScores(
  children,
  weightContext,
  pathToNode,
  questionSetRollups
) {
  const scores = {};

  for (const { questionSetKey, questionKeys } of questionSetRollups) {
    const items = children.map((child) => ({
      score: questionSetScoreFromFlatMap(
        child.scores ?? {},
        questionSetKey,
        questionKeys
      ),
      weight: childWeight(weightContext, child, pathToNode),
    }));

    scores[questionSetRollupKey(questionSetKey)] = {
      score: rollupSiblingScores(items, {
        getScore: (item) => item.score,
        getWeight: (item) => item.weight,
      }),
      question_set_key: questionSetKey,
    };
  }

  return scores;
}

/**
 * Per-question rollup for SquadScorecard-style flat score maps.
 */
export function rollupChildScoreMaps(children, weightContext, pathToNode) {
  const keys = new Set();

  for (const child of children) {
    for (const key of Object.keys(child.scores ?? {})) {
      keys.add(key);
    }
  }

  const scores = {};

  for (const combinedKey of keys) {
    const items = children.map((child) => ({
      score: child.scores?.[combinedKey]?.score ?? null,
      weight: childWeight(weightContext, child, pathToNode),
      question_key: child.scores?.[combinedKey]?.question_key,
      question_set_key: child.scores?.[combinedKey]?.question_set_key,
    }));

    const score = rollupSiblingScores(items, {
      getScore: (item) => item.score,
      getWeight: (item) => item.weight,
    });

    const sample = items.find((i) => i.question_key != null) ?? items[0];

    scores[combinedKey] = {
      score,
      question_key: sample?.question_key,
      question_set_key: sample?.question_set_key,
    };
  }

  return scores;
}
